'use client';

import { BackgroundBlur } from '@livekit/track-processors';
import { useLayoutEffect, useMemo, useState, useCallback } from 'react';

import { createContext } from '@/context/createContext';

import type { LocalVideoTrack } from 'livekit-client';
import type { FC, PropsWithChildren } from 'react';

type BlurBackgroundProps = {
  isBlurred: boolean;
  videoTrack: LocalVideoTrack;
};

type BackgroundEffectContext = {
  isBlurSupported: boolean;
  isBackgroundBlurred: boolean;
  setIsBackgroundBlurred: (isBackgroundBlurred: boolean) => void;
  blurBackground: (props: BlurBackgroundProps) => Promise<void>;
};

const [BackgroundEffectContextProvider, useBackgroundEffectContext] = createContext<BackgroundEffectContext>({
  name: 'BackgroundEffectContext',
});

const BackgroundEffectProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isBlurSupported, setIsSupported] = useState(false);
  const [isBackgroundBlurred, setIsBackgroundBlurred] = useState(false);

  useLayoutEffect(() => {
    try {
      BackgroundBlur();
      setIsSupported(true);
    } catch {
      setIsSupported(false);
    }
  }, []);

  // isBlurred passed separately to isBackgroundBlurred so this function can be used regardless of existing state (in the case we need to sync livekit-track isBlurred state with local isBlurred state)
  const blurBackground = useCallback(async ({ isBlurred, videoTrack }: BlurBackgroundProps) => {
    const blur = BackgroundBlur(15);
    if (isBlurred) {
      await videoTrack.stopProcessor();
    } else {
      await videoTrack.setProcessor(blur);
    }
  }, []);

  const value = useMemo(() => {
    return {
      isBlurSupported,
      isBackgroundBlurred,
      setIsBackgroundBlurred,
      blurBackground,
    };
  }, [isBlurSupported, isBackgroundBlurred, setIsBackgroundBlurred, blurBackground]);

  return <BackgroundEffectContextProvider value={value}>{children}</BackgroundEffectContextProvider>;
};

export { useBackgroundEffectContext, BackgroundEffectProvider };
